<template>
  <div class="player-wrapper">
    <video
      :id="id"
      ref="hlsVideo"
      :controls="controls"
      autoplay
      muted
      class="player-video"
      @playing="() => this.$emit('played', this.$refs.hlsVideo)"
    ></video>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  </div>
</template>

<script>
import Hls from "hls.js";
export default {
  props: {
    width: { default: 750, type: Number },
    height: { default: 540, type: Number },
    controls: { default: true, type: Boolean },
    src: String,
    id: Number,
  },
  data() {
    return {
      hls: null,
      isLoading: false,
      errorMessage: "",
      maxRetries: 3,
      retryCount: 0,
      liveSyncInterval: null,
    };
  },
  watch: {
    src() {
      this.loadHls();
    },
  },
  methods: {
    loadHls() {
      if (!this.$refs.hlsVideo) return;

      this.isLoading = true;
      this.errorMessage = "";
      this.retryCount = 0;

      if (this.hls) this.hls.destroy();
      if (this.liveSyncInterval) clearInterval(this.liveSyncInterval);

      if (Hls.isSupported()) {
        this.hls = new Hls({
          // -- Live edge --
          liveSyncDurationCount: 3, // play 3 segments behind live edge (6s)
          liveMaxLatencyDurationCount: 5, // jump forward if >10s behind
          liveBackBufferLength: 10, // keep 10s of back buffer, discard the rest

          // -- Buffering --
          maxBufferLength: 6, // buffer up to 6s ahead
          maxMaxBufferLength: 12, // hard cap matches your TARGET_LATENCY_S
          maxBufferSize: 30 * 1024 * 1024, // 30MB buffer cap
          maxBufferHole: 0.5, // tolerate small gaps without stalling

          // -- Loading --
          manifestLoadingTimeOut: 8000,
          manifestLoadingMaxRetry: 6,
          levelLoadingTimeOut: 8000,
          fragLoadingTimeOut: 8000,
          fragLoadingMaxRetry: 4,

          // -- Latency chasing --
          highBufferWatchdogPeriod: 2, // check for stalls every 2s
          nudgeOffset: 0.2, // small nudge on stall
          nudgeMaxRetry: 5,

          // -- ABR (single-quality, but safe defaults) --
          startLevel: -1, // auto-select
          abrEwmaDefaultEstimate: 1_000_000, // 1 Mbps initial estimate

          // -- No caching (matches hls_allow_cache 0) --
          enableWorker: true,
          lowLatencyMode: false, // true only for LL-HLS (PART/PRELOAD), not standard HLS
        });

        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.isLoading = false;
          this.startLiveSync();
          this.emitBufferUpdate();
        });

        this.hls.on(Hls.Events.BUFFER_APPENDED, () => {
          this.emitBufferUpdate();
          this.nudgePlayback(this.$refs.hlsVideo);
        });

        this.hls.on(Hls.Events.ERROR, (event, data) => {
          console.error("HLS Error:", data);
          this.$emit("error", data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (this.retryCount < this.maxRetries) {
                  this.retryCount++;
                  setTimeout(() => this.loadHls(), 2000);
                } else {
                  this.errorMessage =
                    "Network error: Unable to load stream after retries.";
                  this.isLoading = false;
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                this.hls?.recoverMediaError();
                break;
              default:
                this.errorMessage = "Fatal error: Unable to load stream.";
                this.isLoading = false;
                break;
            }
          }
        });

        this.hls.loadSource(this.src);
        this.hls.attachMedia(this.$refs.hlsVideo);
      } else if (this.$refs.hlsVideo.canPlayType("application/vnd.apple.mpegurl")) {
        this.$refs.hlsVideo.src = this.src;
        this.$refs.hlsVideo.addEventListener("loadedmetadata", () => {
          this.isLoading = false;
        });
        this.$refs.hlsVideo.addEventListener("error", () => {
          this.errorMessage = "Error loading native HLS stream.";
          this.isLoading = false;
        });
      }
    },
    nudgePlayback(video) {
      if (video.readyState < 3) {
        video.currentTime = video.currentTime + 0.001;
      }
      const playPromise = video.play?.();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(() => {});
      }
    },
    startLiveSync() {
      if (this.liveSyncInterval) clearInterval(this.liveSyncInterval);
      this.liveSyncInterval = setInterval(() => {
        const video = this.$refs.hlsVideo;
        if (this.hls && video && this.hls.liveSyncPosition !== undefined) {
          video.currentTime = this.hls.liveSyncPosition;
        }
      }, 5000);
    },
    emitBufferUpdate() {
      const video = this.$refs.hlsVideo;
      if (video && video.buffered.length > 0) {
        const bufferLength = video.buffered.end(0) - video.buffered.start(0);
        this.$emit("bufferUpdate", bufferLength);
      }
    },
  },
  mounted() {
    const video = this.$refs.hlsVideo;
    if (video) this.loadHls();
  },
  beforeDestroy() {
    if (this.hls) this.hls.destroy();
    if (this.liveSyncInterval) clearInterval(this.liveSyncInterval);
  },
};
</script>

<style scoped>
.player-wrapper {
  /* Take full size provided by parent card */
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center; /* center the video */
  overflow: hidden;
}

.player-video {
  /* Scale within the available area */
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* keep aspect ratio, show background as border */
  display: block;
}

.error-message {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 14px;
}
</style>
